import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  useScrollTrigger,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Spa,
  ShoppingCart,
  Favorite,
  Person,
  Logout,
} from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { label: 'Home', href: '#home', type: 'hash' },
  { label: 'About', href: '#about', type: 'hash' },
  { label: 'Services', href: '#services', type: 'hash' },
  { label: 'Shop', href: '/shop', type: 'route' },
  { label: 'Testimonials', href: '#testimonials', type: 'hash' },
  { label: 'Contact', href: '#contact', type: 'hash' },
];

const Navigation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { getCartCount, setIsCartOpen } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleUserMenuClose();
    navigate('/profile');
  };

  const handleWishlist = () => {
    handleUserMenuClose();
    navigate('/wishlist');
  };

  const handleOrders = () => {
    handleUserMenuClose();
    navigate('/orders');
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/');
  };

  const handleNavClick = (item) => {
    setDrawerOpen(false);

    if (item.type === 'route') {
      navigate(item.href);
      window.scrollTo(0, 0);
    } else {
      // For hash links, check if we're on homepage
      if (window.location.pathname !== '/') {
        // Navigate to homepage first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(item.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Already on homepage, just scroll
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={trigger ? 4 : 0}
        sx={{
          bgcolor: trigger ? 'background.paper' : 'transparent',
          transition: 'all 0.3s ease',
          backdropFilter: trigger ? 'blur(10px)' : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }} className='nav-custom'>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/veda-vine-logo.png`}
                alt="Veda & Vine"
                sx={{
                  height: 45,
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 700,
                  color: trigger ? 'text.primary' : 'text.primary',
                  letterSpacing: '0.05em',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Veda & Vine
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => handleNavClick(item)}
                    sx={{
                      color: trigger ? 'text.primary' : 'text.primary',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}

                {isAuthenticated ? (
                  <>
                    <IconButton
                      onClick={() => navigate('/wishlist')}
                      sx={{
                        color: trigger ? 'text.primary' : 'text.primary',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      <Favorite />
                    </IconButton>
                    <IconButton
                      onClick={() => setIsCartOpen(true)}
                      sx={{
                        color: trigger ? 'text.primary' : 'text.primary',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      <Badge badgeContent={getCartCount()} color="primary">
                        <ShoppingCart />
                      </Badge>
                    </IconButton>
                    <Box
                      onClick={handleUserMenuOpen}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        px: 1,
                        py: 0.5,
                        borderRadius: 50,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: 'primary.main',
                          fontSize: '0.9rem',
                        }}
                      >
                        {user?.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: trigger ? 'text.primary' : 'text.primary',
                        }}
                      >
                        {user?.name}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <IconButton
                      onClick={() => setIsCartOpen(true)}
                      sx={{
                        color: trigger ? 'text.primary' : 'text.primary',
                        ml: 1,
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      <Badge badgeContent={getCartCount()} color="primary">
                        <ShoppingCart />
                      </Badge>
                    </IconButton>
                    <Button
                      onClick={() => navigate('/login')}
                      variant="contained"
                      sx={{
                        borderRadius: 50,
                        px: 3,
                        background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                        },
                      }}
                    >
                      Login
                    </Button>
                  </>
                )}
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  onClick={() => setIsCartOpen(true)}
                  sx={{ color: trigger ? 'text.primary' : 'text.primary' }}
                >
                  <Badge badgeContent={getCartCount()} color="primary">
                    <ShoppingCart className='shop_icon' />
                  </Badge>
                </IconButton>
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{ color: trigger ? 'text.primary' : 'text.primary' }}
                >
                  <MenuIcon className='menu_icon'/>
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {isAuthenticated && (
            <>
              <ListItem>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                    }}
                  >
                    {user?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {user?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>
              </ListItem>
              <Divider />
              <ListItem disablePadding>
                <ListItemButton onClick={handleProfile}>
                  <Person className='font13' sx={{ mr: 1 }} />
                  <ListItemText primary="Profile" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={handleWishlist}>
                  <Favorite className='font12' sx={{ mr: 1 }} />
                  <ListItemText primary="Wishlist" />
                </ListItemButton>
              </ListItem>
              <Divider />
            </>
          )}

          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton onClick={() => handleNavClick(item)}>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    className: "cst-menu",
                    fontWeight: 500,
                    fontSize: '1.1rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

          {isAuthenticated ? (
            <>
              <Divider />
              <ListItem disablePadding>
                <ListItemButton onClick={handleLogout}>
                  <Logout className='font13' sx={{ mr: 1 }} />
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <Divider />
              <ListItem disablePadding>
                <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/login'); }}>
                  <Person sx={{ mr: 2 }} />
                  <ListItemText primary="Login" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Signed in as
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {user?.name}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleOrders}>
          <ShoppingCart sx={{ mr: 1 }} fontSize="small" />
          My Orders
        </MenuItem>
        <MenuItem onClick={handleProfile}>
          <Person sx={{ mr: 1 }} fontSize="small" />
          Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 1 }} fontSize="small" />
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default Navigation;
